<template>
  <div class="mobile-slider">
    <div class="main-photo">
      <div class="carousel-container">
        <!-- Mobile slider with swipe support -->
        <!-- TODO: Consider replacing with keen-slider component when available for better performance and consistency -->
        <div
          class="mobile-slider-container"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
        >
          <div class="slider-track" :style="{ transform: `translateX(-${activeIndex * 100}%)` }">
            <div
              class="slide-item"
              v-for="(photo, index) in photos"
              :key="index"
              :class="{ active: activeIndex === index }"
            >
              <img :src="photo.high" :alt="alt" @click="onMainImageClick" class="slide-image" />
            </div>
          </div>
        </div>

        <ProductCircularView
          v-if="props.isShownCircular"
          :circularPhotos="props.circularPhotos || []"
          :isCardViewOpenedProp="true"
          :fullscreenEnable="false"
          @close="close"
          @setZIndex="() => {}"
        />

        <ProductVideo
          v-else-if="props.isShownVideo"
          :videoURL="props.video || ''"
          :isMutedProp="true"
          :isPlayingProp="true"
          :isCardViewOpenedProp="true"
          :fullscreenEnable="false"
          @close="close"
          @setZIndex="() => {}"
        />

        <!-- TODO: Replace with ButtonUi from @mm-frontend/frontend-ui-kit when available -->
        <button
          @click="likeProduct"
          :class="`favorite ${isFavourite ? 'favorite_active' : ''}`"
          size="large"
        >
          <IconFavoriteFilled v-if="isFavourite" :size="32" />
          <IconFavorite v-else :size="32" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// ProductVideo component implemented and integrated
// TODO: Replace with ProductCircularView from @/shared/components/ui/product-circular-view/index.vue when available
// TODO: Replace with ImgWithFallback from @/shared/ui/ImgWithFallback.vue when available
// TODO: Consider replacing custom slider implementation with keen-slider component when available for better performance and consistency
// TODO: Replace with ButtonUi, IconUi from @mm-frontend/frontend-ui-kit when available

import { ref, computed, watch } from 'vue';
import type { IPhoto } from '../../models/slider';
import { useProductPage } from '../../business/use-product-page';
import { useProductModals } from '../../business/useProductModals';
import ProductVideo from '../common/ProductVideo.vue';
import ProductCircularView from '../common/ProductCircularView.vue';
import { IconFavorite, IconFavoriteFilled } from '../../../ui-kit/icons';

// Get product page instance
const { state, likeProduct } = useProductPage();
const { openGalleryPopup } = useProductModals();

const TRACK_WIDTH = 72;
const THUMB_WIDTH = 16;

interface Props {
  photos: IPhoto[];
  circularPhotos: string[];
  video?: string | null;
  isShownCircular: boolean;
  isShownVideo: boolean;
  alt: string;
  activePhotoKey?: string | null;
}

interface Emits {
  (e: 'active-photo-change', photoKey: string): void;
  (e: 'closeCircular'): void;
  (e: 'closeVideo'): void;
  (e: 'changeStyleThumbIndicator', style: any): void;
}

const props = withDefaults(defineProps<Props>(), {
  photos: () => [],
  circularPhotos: () => [],
  video: null,
  isShownCircular: false,
  isShownVideo: false,
  alt: '',
  activePhotoKey: null,
});

const emit = defineEmits<Emits>();

const scrollTop = ref(0);
const currentIndex = ref(0);
const styleThumbIndicator = ref({
  transform: `translateX(0px)`,
});

const activeIndex = computed((): number => {
  return props.activePhotoKey
    ? props.photos.findIndex((photo) => photo.photoKey === props.activePhotoKey)
    : currentIndex.value;
});

const thumbStep = computed(() => {
  return (TRACK_WIDTH - THUMB_WIDTH) / (props.photos.length - 1);
});

const onActivePhotoChange = (photoKey: string): void => {
  emit('active-photo-change', photoKey);
};

const close = () => {
  emit('closeCircular');
  emit('closeVideo');
};

const onMainImageClick = () => {
  openGalleryPopup(props.photos, activeIndex.value, props.alt, (activeIndex: number) => {
    pageChanged(activeIndex);
  });
};

const pageChanged = (index: number) => {
  currentIndex.value = index;
  // TODO: Update isShownCircular state when available
  onActivePhotoChange(props.photos[index]?.photoKey);
};

// Touch handling for swipe
let startX = 0;
let startY = 0;
let isSwipe = false;

const onTouchStart = (e: TouchEvent) => {
  startX = e.touches[0].clientX;
  startY = e.touches[0].clientY;
  isSwipe = false;
};

const onTouchMove = (e: TouchEvent) => {
  if (!e.touches[0]) return;

  const deltaX = e.touches[0].clientX - startX;
  const deltaY = e.touches[0].clientY - startY;

  // Determine if this is a horizontal swipe
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    isSwipe = true;
    e.preventDefault(); // Prevent vertical scroll
  }
};

const onTouchEnd = (e: TouchEvent) => {
  if (!isSwipe || !e.changedTouches[0]) return;

  const deltaX = e.changedTouches[0].clientX - startX;
  const threshold = 50; // Minimum swipe distance

  if (Math.abs(deltaX) > threshold) {
    if (deltaX > 0) {
      // Swipe right - go to previous
      goToPrevious();
    } else {
      // Swipe left - go to next
      goToNext();
    }
  }
};

const goToPrevious = () => {
  if (currentIndex.value > 0) {
    pageChanged(currentIndex.value - 1);
  }
};

const goToNext = () => {
  if (currentIndex.value < props.photos.length - 1) {
    pageChanged(currentIndex.value + 1);
  }
};

const isFavourite = computed((): boolean => {
  return state.isFavorite || false;
});

// TODO: Implement checkIfCanReply functionality when available
// const canReply = ref(false);
// onMounted(async () => {
//   if (productData.value?.id) {
//     // this.canReply = await dataService.checkIfCanReply(this.productData.id);
//   }
// });

watch(activeIndex, (val: number) => {
  currentIndex.value = val;
  styleThumbIndicator.value = {
    transform: `translateX(${val * thumbStep.value}px)`,
  };

  emit('changeStyleThumbIndicator', styleThumbIndicator.value);
});

// Watch for external changes to activePhotoKey (when SKU changes)
watch(
  () => props.activePhotoKey,
  (newPhotoKey) => {
    if (newPhotoKey) {
      const newIndex = props.photos.findIndex((photo) => photo.photoKey === newPhotoKey);
      if (newIndex !== -1 && newIndex !== currentIndex.value) {
        currentIndex.value = newIndex;
      }
    }
  }
);
</script>

<style lang="stylus">
.mobile-slider
  position sticky;
  top 0;
  width 100%
  display flex

  @supports (-webkit-touch-callout: none) {
    position relative;
  }
  .slide-wrapper
    width 100%
    height 100%
    overflow-y hidden
    position relative
    .slide-item
      height 100%

  .main-photo
    width 100%
    height 100%
    position relative
    background Gray(A10)
    overflow hidden
    user-select none

    .carousel-container
      position relative
      display flex
      align-items center
      overflow hidden
      padding-top calc(100% * 4.26 / 3.2)
      width 100%
      height 0
      background-color #eee

      .mobile-slider-container
        position absolute
        top 0
        right 0
        bottom 0
        left 0
        width 100%
        height 100%
        overflow hidden

        .slider-track
          display flex
          width 100%
          height 100%
          transition transform 0.3s ease-out

          .slide-item
            flex 0 0 100%
            width 100%
            height 100%

            .slide-image
              width 100%
              height 100%
              object-fit cover
              object-position center
              user-select none
              cursor pointer

    .image
      width 100%
      height 100%
      border-radius 20px
      position absolute
      cursor pointer
      top 0
      left 0
      right 0
      bottom 0

.favorite_active
  --color #E53935 !important
  path
    fill #E53935
</style>

<style lang="stylus" scoped>
.favorite
  background none
  position absolute
  top 12px
  right 12px
  cursor pointer
</style>
